import React, { useState, useEffect, useRef } from 'react';
import "semantic-ui-css/semantic.min.css";
import "./OutboundCampaign.css";
import api from '../../../api';
import Papa from "papaparse";
import { Icon } from "semantic-ui-react";
import Modal from '../ChatSetup/components/Modal';
import ConfirmationModal from '../Workspace/Botbuilder/Details/confusedbranch/DataConnect/ConfirmationModal';
import { scrollToFirstError } from '../../../util_funs';
import { Message } from "semantic-ui-react";
import RadioOptionGroup from './RadioOptionGroup';
import CSVSampleDownloadButton from './CSVSampleDownloadButton';
import WeekdaySelector from './WeekdaySelector';
import CustomerTable from './customers/CustomerTable';
import ManualCustomerEntry from './customers/ManualCustomerEntry';
import CustomerDetailsModalBody from './customers/CustomerDetailsModalBody';
import { newClientRowKey, ensureClientRowKey } from './customers/bulkCustomerUtils';
import { useCampaignFormBulkSelection } from './customers/useCampaignFormBulkSelection';
import DailyWeeklyScheduleFields from './CampaignScheduleFields/DailyWeeklyScheduleFields';
import PerDayTimeFrameEditor from './CampaignScheduleFields/PerDayTimeFrameEditor';
import HolidayExclusionSection from './HolidayExclusionSection';
import VoicemailRetrySettings from './VoicemailRetrySettings';
import DuplicateDataRuleSection, { DUPLICATE_DATA_RULE_MAX_DAYS } from './DuplicateDataRuleSection';
import CallGroupWebhookToggleSection from './CallGroupWebhookToggleSection';
import {
    applyOngoingDefaults,
    isStartTimeInPastForToday,
    resetScheduledFields,
    updateTimesForTimezone,
    formatDateForInput,
    isDateInPast,
    normalizePhoneNumber
} from './scheduleUtils';
import { canEditField } from './campaignEditRules';
import constants from '../../../../src/util_funs/constant';
import useTrustHubValidation from '../../../hooks/useTrustHubValidation';
import TrustHubValidationModal from '../TrustHubDashboard/components/shared/TrustHubValidationModal';


const phoneRegex = /^\(\d{3}\)\d{3}-\d{4}$/;
const nameRegex = /^(?!\d+$)(?!.*[.\-'\s]{2})[\p{L}0-9][\p{L}0-9.\-'\s]{0,48}[\p{L}0-9.]$/u;

const headers = ['Name', 'Phone'];
const sampleData = [
    ['John Doe', '(123)456-7890'],
    ['Jane Smith', '(098)765-4321'],
    ['Mike Johnson', '(112)233-4455'],
    ['Sarah Wilson', '(555)123-4567']
];

/**
 * When the dropdown has no valid selection, choose:
 * 1) campaign's assigned id if it appears in the list
 * 2) else first number with number_type outbound
 * 3) else first row (e.g. inbound-only fallback list)
 */
function pickDefaultOutboundTwilioNumberId(numbers, campaignAssignedId) {
    if (!Array.isArray(numbers) || numbers.length === 0) return '';
    const idStr = (v) => (v == null || v === '' ? '' : String(v));
    const ids = new Set(numbers.map((n) => idStr(n.id)));
    const assigned = idStr(campaignAssignedId);
    if (assigned && ids.has(assigned)) {
        return assigned;
    }
    const firstOutbound = numbers.find(
        (n) => String(n.number_type || '').toLowerCase() === 'outbound'
    );
    if (firstOutbound) return idStr(firstOutbound.id);
    return idStr(numbers[0].id);
}

const CampaignForm = ({
    onSave,
    onCancel,
    workspace_id,
    user_id,
    campaign_id,
    editingCampaign,
   compactMode = false
}) => {
    const [formData, setFormData] = useState({
        name: '',
        workspace_id: '',
        outbound_twilio_number_id: '',
        caller_id_id: '',
        campaign_category: '',
        consent_type: '',
        consent_notes: '',
        type: 'manual',
        webhook_url: '',
        webhook_queue_delay_days: 0,
        send_type: 'now',
        schedule_type: 'one_time',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        days_of_week: [],
        timezone: '',
        holidays: [],
        voicemail_retry_enabled: false,
        voicemail_retry_delay_days: 2,
        voicemail_retry_max_attempts: 3,
        voicemail_retry_apply_to_send_now: false,
        duplicate_data_rule_enabled: false,
        duplicate_data_rule_days: '',
        voicemail_retry_delay_days: 1,
        voicemail_retry_max_attempts: 1,
        voicemail_retry_apply_to_send_now: false,
        /** Triggered only: individual | webhook_inline | saved_template */
        call_group_mode: 'individual',
        /** Per-day time frame configuration */
        enable_per_day_timeframes: false,
        day_timeframes: {},
    });


    const [customers, setCustomers] = useState([]);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [csvProcessing, setCsvProcessing] = useState(false);
    const [message, setMessage] = useState(null);
    const [showCSVModal, setShowCSVModal] = useState(false);
    const [csvFile, setCSVFile] = useState(null);
    const [csvError, setCSVError] = useState("");
    const [csvSuccess, setCSVSuccess] = useState("");
    const [showOptOutConfirmModal, setShowOptOutConfirmModal] = useState(false);
    const [optOutReenableCustomer, setOptOutReenableCustomer] = useState(null);
    const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customersPage, setCustomersPage] = useState(1);
    const [customersPerPage] = useState(10);
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [manualCustomers, setManualCustomers] = useState({ name: '', phone_number: '', manual: true, caller_opted_out: false })
    const [activeOutboundWorkspaces, setActiveOutboundWorkspaces] = useState([]);
    const [availableOutboundNumbers, setAvailableOutboundNumbers] = useState([]);
    const [outboundNumbersLoading, setOutboundNumbersLoading] = useState(false);
    const [outboundNumbersMessage, setOutboundNumbersMessage] = useState('');
    const [availableCallerIds, setAvailableCallerIds] = useState([]);
    const [callerIdsLoading, setCallerIdsLoading] = useState(false);
    const [callerIdsMessage, setCallerIdsMessage] = useState('');
    const [originalData, setOriginalData] = useState(null);
    const fileInputRef = useRef(null);
    const errorMessageRef = useRef(null);
    const nestedErrorsRef = useRef(null);
    /** Staged manual-entry rows (not yet active). Pass current list, e.g. `prev` inside setCustomers. */
    const getManualCustomers = (list = customers) =>
        list.filter((c) => c.manual && c.status !== "active");


    const getTotalCustomers = () => customers.length;

    // Load active outbound workspaces
    const loadActiveOutboundWorkspaces = async () => {
        try {
            setLoading(true);
            const response = await api.outbound.getActiveOutboundWorkspaces({
                user_id: user_id,
            });
            if (response && response.status && response.data) {
                setActiveOutboundWorkspaces(response.data);
            }
        } catch (error) {
            console.error("Error loading active outbound workspaces:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableOutboundNumbers = async () => {
        try {
            setOutboundNumbersLoading(true);
            const response = await api.outbound.getAvailableOutboundNumbers();
            if (response && response.status) {
                const numbers = Array.isArray(response.data) ? response.data : [];
                setAvailableOutboundNumbers(numbers);
                setOutboundNumbersMessage(
                    numbers.length === 0
                        ? 'No outbound numbers are configured. Please contact support.'
                        : ''
                );
            } else {
                setAvailableOutboundNumbers([]);
                setOutboundNumbersMessage(response?.message || 'Unable to load outbound numbers.');
            }
        } catch (error) {
            console.error("Error loading outbound numbers:", error);
            setAvailableOutboundNumbers([]);
            setOutboundNumbersMessage('Unable to load outbound numbers.');
        } finally {
            setOutboundNumbersLoading(false);
        }
    };

    const loadAvailableCallerIds = async () => {
        try {
            setCallerIdsLoading(true);
            const response = await api.outbound.getAvailableCallerIds();
            if (response && response.status) {
                const ids = Array.isArray(response.data) ? response.data : [];
                setAvailableCallerIds(ids);
                setCallerIdsMessage(ids.length === 0 && availableOutboundNumbers.length === 0 ? 'No verified Caller IDs ready for calling. Verify a number in Trust Hub.' : '');
            } else {
                setAvailableCallerIds([]);
                setCallerIdsMessage(response?.message || 'Unable to load caller IDs.');
            }
        } catch (error) {
            setAvailableCallerIds([]);
            setCallerIdsMessage('Unable to load caller IDs.');
        } finally {
            setCallerIdsLoading(false);
        }
    };

    // Fetch account default timezone
    const fetchDefaultTimezone = async () => {
        try {
            const response = await api.outbound.getAccountDefaultTimezone({
                user_id: user_id,
            });
            if (response && response.status && response.data && response.data.timezone) {
                // Set timezone in form only if this is a new campaign (not editing)
                if (!editingCampaign) {
                    setFormData(prev => ({
                        ...prev,
                        timezone: response.data.timezone
                    }));
                }
            }
        } catch (error) {
            console.error("Error loading default timezone:", error);
            // Fallback to browser timezone if API fails
            if (!editingCampaign) {
                setFormData(prev => ({
                    ...prev,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }));
            }
        }
    };

    useEffect(() => {
        loadActiveOutboundWorkspaces();
        loadAvailableOutboundNumbers();
        loadAvailableCallerIds();
        fetchDefaultTimezone();
    }, []);


    // Populate form data when editing an existing campaign
    useEffect(() => {
        if (editingCampaign) {
            const formattedStartDate = formatDateForInput(editingCampaign.schedule?.start_date);
            const formattedEndDate = formatDateForInput(editingCampaign.schedule?.end_date);

            const formDataToSet = {
                name: editingCampaign.name || '',
                workspace_id: editingCampaign.workspace_id,
                outbound_twilio_number_id: editingCampaign.outbound_twilio_number_id || '',
                caller_id_id: editingCampaign.caller_id_id || '',
                campaign_category: editingCampaign.campaign_category,
                consent_type: editingCampaign.consent_type || '',
                consent_notes: editingCampaign.consent_notes || '',
                type: editingCampaign.type || 'manual',
                webhook_url: editingCampaign.webhook_url || '',
                webhook_queue_delay_days: editingCampaign.webhook_queue_delay_days ?? 0,
                send_type: editingCampaign.send_type || 'now',
                schedule_type: editingCampaign.schedule?.schedule_type || 'one_time',
                start_date: formattedStartDate,
                end_date: formattedEndDate,
                days_of_week: editingCampaign.schedule?.days || '',
                timezone: editingCampaign.schedule?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                start_time: editingCampaign.schedule?.start_time,
                end_time: editingCampaign.schedule?.end_time,
                status: editingCampaign.status,
                holidays: (editingCampaign.holidays || []).map(holiday => {
                    // Normalize date to YYYY-MM-DD format for calendar modal
                    const date = new Date(holiday.date);
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return {
                        ...holiday,
                        date: `${year}-${month}-${day}`
                    };
                }),
                voicemail_retry_enabled: editingCampaign.voicemail_retry_enabled || false,
                voicemail_retry_delay_days: editingCampaign.voicemail_retry_delay_days || 2,
                voicemail_retry_max_attempts: editingCampaign.voicemail_retry_max_attempts || 3,
                voicemail_retry_apply_to_send_now: editingCampaign.voicemail_retry_apply_to_send_now || false,
                duplicate_data_rule_enabled: !!editingCampaign.duplicate_data_rule_enabled,
                duplicate_data_rule_days:
                    editingCampaign.duplicate_data_rule_enabled &&
                    editingCampaign.duplicate_data_rule_days != null &&
                    editingCampaign.duplicate_data_rule_days !== ''
                        ? Number(editingCampaign.duplicate_data_rule_days)
                        : '',
                voicemail_retry_delay_days: editingCampaign.voicemail_retry_delay_days || 1,
                voicemail_retry_max_attempts: editingCampaign.voicemail_retry_max_attempts || 1,
                voicemail_retry_apply_to_send_now: editingCampaign.voicemail_retry_apply_to_send_now || false,
                call_group_mode:
                    editingCampaign.call_group_mode === 'webhook_inline'
                        ? 'webhook_inline'
                        : editingCampaign.call_group_mode === 'saved_template'
                          ? 'saved_template'
                          : 'individual',
                enable_per_day_timeframes: editingCampaign.enable_per_day_timeframes || editingCampaign.schedule?.enable_per_day_timeframes || false,
                day_timeframes: editingCampaign.day_timeframes || editingCampaign.schedule?.day_timeframes || {},
            };
            setFormData(formDataToSet);
            setOriginalData(editingCampaign);
            if (editingCampaign.customers && editingCampaign.customers.length > 0) {
                setCustomers(editingCampaign.customers.map(ensureClientRowKey));
            } else {
                setCustomers([]);
            }

            // Fetch full campaign details so customers include dynamic_variables (list API does not return them)
            if (editingCampaign.id) {
                const tz = editingCampaign.schedule?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                api.outbound.getCampaign(editingCampaign.id, tz).then((response) => {
                    if (!response?.status || !response.data) return;
                    if (response.data.customers?.length > 0) {
                        setCustomers(response.data.customers.map(ensureClientRowKey));
                    }
                    const d = response.data;
                    if (d.duplicate_data_rule_enabled !== undefined || d.duplicate_data_rule_days !== undefined) {
                        setFormData((prev) => ({
                            ...prev,
                            outbound_twilio_number_id: d.outbound_twilio_number_id || prev.outbound_twilio_number_id || '',
                            caller_id_id: d.caller_id_id != null ? d.caller_id_id : prev.caller_id_id,
                            duplicate_data_rule_enabled: !!d.duplicate_data_rule_enabled,
                            duplicate_data_rule_days:
                                d.duplicate_data_rule_enabled &&
                                d.duplicate_data_rule_days != null &&
                                d.duplicate_data_rule_days !== ''
                                    ? Number(d.duplicate_data_rule_days)
                                    : '',
                        }));
                    }
                    if (
                        d.call_group_mode === 'webhook_inline' ||
                        d.call_group_mode === 'individual' ||
                        d.call_group_mode === 'saved_template'
                    ) {
                        setFormData((prev) => ({
                            ...prev,
                            call_group_mode:
                                d.call_group_mode === 'webhook_inline'
                                    ? 'webhook_inline'
                                    : d.call_group_mode === 'saved_template'
                                      ? 'saved_template'
                                      : 'individual',
                        }));
                    }
                    // Update timeframe data from full campaign response
                    if (d.enable_per_day_timeframes !== undefined || d.day_timeframes !== undefined) {
                        setFormData((prev) => ({
                            ...prev,
                            enable_per_day_timeframes: d.enable_per_day_timeframes || false,
                            day_timeframes: d.day_timeframes || {},
                        }));
                    }
                }).catch(() => {});
            }
        }
    }, [editingCampaign]);

    const {
        bulkSelection,
        showBulkConfirmModal,
        setShowBulkConfirmModal,
        confirmBulkDelete,
    } = useCampaignFormBulkSelection({
        customers,
        setCustomers,
        customersPage,
        customersPerPage,
        campaignStatus: formData.status,
        campaignId: campaign_id,
        setMessage,
        errorMessageRef,
        editCampaignMode: !!editingCampaign,
    });

        // Edit only: auto-fill outbound when empty or value not in list (after numbers fetch).
        useEffect(() => {
            if (!editingCampaign) return;
            if (outboundNumbersLoading || !availableOutboundNumbers.length) return;
            setFormData((prev) => {
                const numbers = availableOutboundNumbers;
                const idStr = (v) => (v == null || v === '' ? '' : String(v));
                const current = idStr(prev.outbound_twilio_number_id);
                const inList = current && numbers.some((n) => idStr(n.id) === current);
                if (inList) return prev;
                const assigned = editingCampaign?.outbound_twilio_number_id;
                const nextId = pickDefaultOutboundTwilioNumberId(numbers, assigned);
                if (!nextId || idStr(nextId) === current) return prev;
                return { ...prev, outbound_twilio_number_id: nextId };
            });
        }, [
            availableOutboundNumbers,
            outboundNumbersLoading,
            editingCampaign,
            editingCampaign?.id,
            editingCampaign?.outbound_twilio_number_id,
        ]);

    // Trust Hub validation hook (AC14 - Profile Validation Before Channel Use)
    const {
        validateTrustHub,
        showValidationModal: showTrustHubModal,
        setShowValidationModal: setShowTrustHubModal,
        profileStatus: trustHubProfileStatus,
    } = useTrustHubValidation(user_id);

    const phoneFormat = (p) => {
        let d = String(p ?? '').replace(/\D/g, '');
        if (d.length === 11 && d[0] === '1') d = d.slice(1);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `(${d.slice(0, 3)})${d.slice(3)}`;
        return `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6, 10)}`;
    };


    const handleChange = (field, value) => {
        if (field === "phone_number") value = phoneFormat(value);
        setManualCustomers(prev => ({ ...prev, [field]: value }));
    };
    const validateCustomer = () => {
        const newErrors = {};

        const name = manualCustomers.name ? manualCustomers.name.trim() : "";
        const phone = manualCustomers.phone_number;

        //Use validateName function here
        const nameErrors = validateName(name);
        if (nameErrors.length > 0) {
            newErrors.name = nameErrors[0]; // pick the first error
        }

        // Phone validations
        if (!phone) {
            newErrors.phone_number = "Phone number is required.";
        } else if (!phoneRegex.test(phone)) {
            newErrors.phone_number = "Valid phone number required ((000)000-0000)";
        }

        // Duplicate check
        // const isDuplicate = customers.some((c) => c.phone_number === phone);
        // if (isDuplicate) {
        //     newErrors.phone_number = "Duplicate phone number.";
        // }

        return newErrors;
    };

    const handleAddCustomer = () => {
        const validationErrors = validateCustomer();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setCustomers(prev => [
            ...prev,
            {
                ...manualCustomers,
                caller_opted_out: false,
                clientRowKey: newClientRowKey(),
                pending_date_added: new Date().toISOString(),
            },
        ]);
        setManualCustomers({ name: '', phone_number: '', manual: true, caller_opted_out: false });
        setErrors([]);
    };

    const clearCSVInputAndErrors = () => {
        setCSVFile(null);
        setCSVError("");
        setCSVSuccess("");
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const autoCloseModalOnError = () => {
        setTimeout(() => {
            clearCSVInputAndErrors();
        }, 3000);
    };

    const viewCustomer = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerDetailsModal(true);
    };

    /** Normalize phone to 10-digit canonical form for comparison (strips non-digits, removes leading 1). */
    const normalizePhoneForCompare = (p) => String(p || '').replace(/\D/g, '').replace(/^1/, '') || '';

    /** Handle opt-out toggle: show duplicate notice if needed, confirm before re-enabling, then perform update. */
    const handleOptOutChange = (customer, callerOptedOut) => {
        const phoneNorm = normalizePhoneForCompare(customer.phone_number);
        const samePhoneCount = customers.filter((c) => normalizePhoneForCompare(c.phone_number) === phoneNorm).length;

        if (callerOptedOut && samePhoneCount > 1) {
            setMessage({
                type: 'info',
                text: 'This phone number appears multiple times in the campaign. The opt-out will apply to all entries.',
            });
            setTimeout(() => setMessage(null), 5000);
        }

        if (!callerOptedOut && customer.caller_opted_out) {
            setOptOutReenableCustomer({ customer, callerOptedOut });
            setShowOptOutConfirmModal(true);
            return;
        }

        performOptOutChange(customer, callerOptedOut);
    };

    const confirmOptOutReenable = () => {
        if (optOutReenableCustomer) {
            performOptOutChange(optOutReenableCustomer.customer, optOutReenableCustomer.callerOptedOut);
            setShowOptOutConfirmModal(false);
            setOptOutReenableCustomer(null);
        }
    };

    /** Revert caller_opted_out for all customers with matching phone when API call fails. */
    const revertOptOutByPhone = (phoneNorm, previousValue) =>
        setCustomers((prev) =>
            prev.map((c) =>
                normalizePhoneForCompare(c.phone_number) === phoneNorm
                    ? { ...c, caller_opted_out: previousValue }
                    : c
            )
        );

    /**
     * Update opt-out status optimistically, then sync to API. Reverts local state if API fails.
     * Applies change to all customers with same phone (duplicates across campaign).
     */
    const performOptOutChange = (customer, callerOptedOut) => {
        const phoneNorm = normalizePhoneForCompare(customer.phone_number);
        const previousValue = !!customer.caller_opted_out;
        setCustomers((prev) =>
            prev.map((c) =>
                normalizePhoneForCompare(c.phone_number) === phoneNorm
                    ? { ...c, caller_opted_out: !!callerOptedOut }
                    : c
            )
        );
        if (customer.id) {
            api.outbound
                .updateCampaignCustomerOptOut(customer.id, callerOptedOut)
                .then((res) => {
                    if (!res.status) {
                        setMessage({ type: 'error', text: res.message || 'Failed to update opt-out status.' });
                        setTimeout(() => setMessage(null), 3000);
                        revertOptOutByPhone(phoneNorm, previousValue); // rollback optimistic update
                    }
                })
                .catch(() => {
                    setMessage({ type: 'error', text: 'Failed to update opt-out status.' });
                    setTimeout(() => setMessage(null), 3000);
                    revertOptOutByPhone(phoneNorm, previousValue); // rollback optimistic update
                });
        }
    };

    function validateName(name) {
        const errors = [];


        if (!name || name.trim() === "") {
            errors.push("Name is missing or blank");
        } else if (name.length < 2) {
            errors.push("The name field must be at least 2 characters.");
        } else if (name.length > 50) {
            errors.push("Name cannot exceed 50 characters.");
        } else if (!nameRegex.test(name.trim())) {
            errors.push("Name can only contain letters, digits, spaces, hyphens, and apostrophes.");
        }

        return errors;
    }

    /**
     * Validate CSV headers
     * @param {Array} headers - Array of CSV headers (from Papa.parse results.meta.fields)
     * @param {Array} expectedOrder - Array of expected headers in order, e.g., ["phone", "name"]
     * @returns {Array} errors - Empty if valid, array of error messages if invalid
     */
    function validateCSVHeaders(headers, requiredHeaders) {
        const errors = [];

        if (!headers || headers.length === 0) {
            errors.push("CSV has no headers.");
            return errors;
        }

        // Trim and lowercase headers for comparison
        const cleanHeaders = headers.map(h => h.trim().toLowerCase());

        // Require name + phone columns, but allow additional (dynamic) columns.
        const missing = requiredHeaders.filter(h => !cleanHeaders.includes(h.toLowerCase()));
        if (missing.length > 0) {
            errors.push(`CSV is missing required column(s): ${missing.join(", ")}.`);
            return errors;
        }

        return errors; // [] if valid
    }


    const handleCSVUpload = () => {
        if (!csvFile) {
            setCSVError("Please select a CSV file.");
            autoCloseModalOnError();
            return;
        }

        const isCSV = csvFile.type === "text/csv" || csvFile.name.endsWith(".csv");
        if (!isCSV) {
            setCSVError("Please upload a valid CSV file (.csv)");
            autoCloseModalOnError();
            return;
        }

        setCsvProcessing(true);
        setCSVError("");
        setCSVSuccess("");

        Papa.parse(csvFile, {
            header: true,
            skipEmptyLines: true,
            transformHeader: function (header) {
                return header.trim();
            },
            complete: function (results) {
                try {
                    const headers = results.meta.fields || [];
                    // Validate headers (allow extra/dynamic columns); required columns checked case-insensitively
                    const headerErrors = validateCSVHeaders(headers, ["name", "phone"]);
                    if (headerErrors.length > 0) {
                        setCSVError(headerErrors);
                        setCsvProcessing(false);
                        return;
                    }
                    const parsed = results.data;
                    if (!parsed || parsed.length === 0) {
                        setCSVError(["CSV file is empty. Please add at least one customer."]);
                        autoCloseModalOnError();
                        setCsvProcessing(false);
                        return;
                    }
                    const validEntries = [];
                    const invalidEntries = [];
                    const duplicateEntries = [];

                    const getRowValue = (row, key) => {
                        const k = Object.keys(row || {}).find((c) => String(c).trim().toLowerCase() === key);
                        return k != null ? row[k] : undefined;
                    };

                    parsed.forEach((row, index) => {
                        const name = getRowValue(row, 'name')?.trim();
                        const phone_number = normalizePhoneNumber(getRowValue(row, 'phone'));
                        const nameErrors = validateName(name);
                        const errors = [...nameErrors];
                        const strictPattern = /^\(\d{3}\)\d{3}-\d{4}$/;
                        // Phone validation
                        if (!phone_number) {
                            errors.push('Phone number is missing or blank.');
                        } else if (phone_number.length !== 10) {
                            errors.push(`Phone number must be 10 digits.`);
                        } 
                        // else if (!strictPattern.test(row.phone.trim())) {
                        //     errors.push(`The phone number must be in the format (123)456-7890.`);
                        // }

                        // Duplicate check in existing customers
                        // if (errors.length === 0) {
                        //     const isDuplicate = customers.some(c =>
                        //         c.manual && normalizePhoneNumber(c.phone_number) === phone_number
                        //     );
                        //     if (isDuplicate) {
                        //         errors.push(`${phone_number} Duplicate phone number.`);
                        //     }
                        // }

                        // if (errors.length === 0) {
                        //     const isDuplicate = customers.some(c => {
                        //         return (c.manual || c.status === "active") && normalizePhoneNumber(c.phone_number) === phone_number;
                        //     });



                        //     if (isDuplicate) {
                        //         errors.push(`${row.phone} Duplicate phone number.`);
                        //     }
                        // }

                        // Duplicate check inside CSV
                        // if (errors.length === 0) {
                        //     const isDuplicateInCSV = validEntries.slice(0, index).some(existing =>
                        //         normalizePhoneNumber(existing.phone_number) === phone_number
                        //     );

                        //     if (isDuplicateInCSV) {
                        //         errors.push(`${row.phone} Duplicate phone number.`);
                        //     }
                        // }

                        // Push into valid or invalid
                        if (errors.length > 0) {
                            invalidEntries.push({
                                row: index + 2,
                                name: name || "Blank",
                                phone_number: getRowValue(row, 'phone') || "Blank",
                                errors,
                            });
                        } else {
                            // Treat any additional CSV columns as dynamic variables; key = header with spaces removed (e.g. "COMPANY Name" -> "COMPANYName")
                            const dynamic_variables = {};
                            Object.keys(row || {}).forEach((key) => {
                                if (!key) return;
                                const trimmedKey = String(key).trim();
                                if (!trimmedKey) return;
                                const keyNoSpaces = trimmedKey.replace(/\s+/g, '');
                                if (!keyNoSpaces) return;
                                if (keyNoSpaces.toLowerCase() === "name" || keyNoSpaces.toLowerCase() === "phone") return;
                                const value = row[key];
                                if (value === undefined || value === null) return;
                                const str = String(value).trim();
                                if (str === "") return;
                                dynamic_variables[keyNoSpaces] = str;
                            });

                            validEntries.push({
                                name,
                                phone_number:phoneFormat(phone_number),
                                manual: false,
                                caller_opted_out: false,
                                clientRowKey: newClientRowKey(),
                                pending_date_added: new Date().toISOString(),
                                ...(Object.keys(dynamic_variables).length > 0 ? { dynamic_variables } : {})
                            });
                        }
                    });



                    if (validEntries.length > 0) {
                        setCustomers((prev) => {
                            const manualCustomers = getManualCustomers(prev);

                            if (editingCampaign) {
                                // Keep every existing row; manual campaigns often use status other than "active".
                                // Old logic kept only status==="active", which dropped rows so CSV looked "not added".
                                return [...prev, ...validEntries];
                            }
                            return [...manualCustomers, ...validEntries];
                        });
                    }

                    if (duplicateEntries.length > 0 || invalidEntries.length > 0) {
                        const allErrors = [];
                        // Add invalid entries first
                        if (invalidEntries.length > 0) {
                            invalidEntries.forEach(invalid => {
                                allErrors.push(`Row ${invalid.row}: ${invalid.errors.join(', ')}`);
                            });
                        }
                        // Add duplicate entries
                        if (duplicateEntries.length > 0) {
                            duplicateEntries.forEach(dup => {
                                allErrors.push(`Row ${dup.row}: ${dup.name} (${dup.phone_number}) - ${dup.reason}`);
                            });
                        }

                        setCSVError(allErrors);
                        setCSVFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = null;
                        }
                    } else if (validEntries.length === 0) {
                        setCSVError([
                            'No valid rows could be imported. Each row needs a name and a valid 10-digit phone number.',
                        ]);
                        setCSVFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = null;
                        }
                    } else {
                        setCSVSuccess('All customers processed successfully.');
                    }
                    // Auto-close modal after appropriate time
                    const hasErrors = invalidEntries.length > 0 || duplicateEntries.length > 0;

                    if (!hasErrors) {
                        const closeTime = 2000; // Only auto-close if no errors
                        setTimeout(() => {
                            setShowCSVModal(false);
                            clearCSVInputAndErrors();
                        }, closeTime);
                    }
                    setCsvProcessing(false);
                    setLoading(false);
                } catch (error) {
                    console.error('Error processing CSV:', error);
                    setCsvProcessing(false);
                    autoCloseModalOnError();
                }
            }
        });
    };


    // Scheduling helpers moved to scheduleUtils.js and imported above

    const handleInputChange = (field, value) => {
        setFormErrors({});
        setErrors([]);
        setFormData(prev => {
            let newData = { ...prev, [field]: value };

            if (field === 'consent_type' && value !== 'other') {
                newData.consent_notes = '';
            }

            if (field === 'type' && value === 'triggered') {
                if (newData.send_type !== 'scheduled') {
                    newData.send_type = 'scheduled';
                }
                newData = applyOngoingDefaults(newData);
            }

            if (field === 'send_type' && value === 'now') {
                newData = resetScheduledFields(newData);
            }


            if (field === 'send_type' && value === 'scheduled') {
                newData = applyOngoingDefaults(newData);
            }

            if (field === 'schedule_type') {
                applyOngoingDefaults(newData);
            }

            return newData;
        });
    };




    const validateForm = () => {
        const errors = {};
        if (!formData.name?.trim()) {
            errors.name = 'Campaign name is required';
        }

        if (!formData.workspace_id) {
            errors.workspace_id = 'Workspace is required';
        }

        if (!formData.outbound_twilio_number_id && !formData.caller_id_id) {
            errors.outbound_twilio_number_id = 'Please select a number.';
        }

        

        if (!formData.campaign_category) {
            errors.campaign_category = 'Campaign Category is required';
        }

        if (!formData.consent_type) {
            errors.consent_type = 'Please select a Consent Type.';
        } else if (formData.consent_type === 'other') {
            const notes = formData.consent_notes?.trim() || '';
            if (!notes) {
                errors.consent_notes = 'Please provide a description for Other consent type.';
            } else if (notes.length < 10) {
                errors.consent_notes = `Description must be at least 10 characters (${notes.length}/10).`;
            } else if (notes.length > 500) {
                errors.consent_notes = `Description must be at most 500 characters (${notes.length}/500).`;
            }
        }

        // Validate holidays
        if (formData.send_type === 'scheduled' && formData.holidays && formData.holidays.length > 0) {
            const hasEmptyHolidayNames = formData.holidays.some(holiday => !holiday.holiday_name || !holiday.holiday_name.trim());
            if (hasEmptyHolidayNames) {
                errors.holidays = 'All holiday dates must have a holiday name.';
            }
        }

        if (formData.type === 'triggered') {
            const raw = formData.webhook_queue_delay_days;
            const d = raw === '' || raw === null || raw === undefined ? 0 : parseInt(raw, 10);
            if (Number.isNaN(d) || d < 0 || d > 365) {
                errors.webhook_queue_delay_days = 'Use a whole number of days from 0 to 365.';
            }
        }

        if (formData.send_type !== 'now' && formData.duplicate_data_rule_enabled) {
            const raw = formData.duplicate_data_rule_days;
            if (raw === '' || raw === null || raw === undefined) {
                errors.duplicate_data_rule_days =
                    'Number of days is required when this rule is enabled.';
            } else {
                const d = parseInt(raw, 10);
                if (Number.isNaN(d) || d < 1 || d > DUPLICATE_DATA_RULE_MAX_DAYS) {
                    errors.duplicate_data_rule_days = `Enter a whole number of days from 1 to ${DUPLICATE_DATA_RULE_MAX_DAYS}.`;
                }
            }
        }

        // Validate voicemail retry settings
        if (formData.voicemail_retry_enabled) {
            const delayDays = parseInt(formData.voicemail_retry_delay_days, 10);
            const maxAttempts = parseInt(formData.voicemail_retry_max_attempts, 10);

            if (!delayDays || isNaN(delayDays)) {
                errors.voicemail_retry_delay_days = 'Retry delay is required when voicemail retry is enabled.';
            } else if (delayDays < 1) {
                errors.voicemail_retry_delay_days = 'Retry delay must be at least 1 day.';
            } else if (delayDays > 30) {
                errors.voicemail_retry_delay_days = 'Retry delay cannot exceed 30 days.';
            }

            if (!maxAttempts || isNaN(maxAttempts)) {
                errors.voicemail_retry_max_attempts = 'Maximum attempts is required when voicemail retry is enabled.';
            } else if (maxAttempts < 1) {
                errors.voicemail_retry_max_attempts = 'Maximum attempts must be at least 1.';
            } else if (maxAttempts > 10) {
                errors.voicemail_retry_max_attempts = 'Maximum attempts cannot exceed 10.';
            }
        }

        if (formData.send_type === 'scheduled') {
            const { schedule_type, start_date, start_time, end_time, timezone, status, enable_per_day_timeframes, day_timeframes } = formData;

            if (!schedule_type) {
                errors.schedule_type = 'Schedule type is required.';
            } else if (schedule_type === 'ongoing' || schedule_type === 'one_time') {
                if (!start_date) errors.start_date = 'Start date is required.';
                if (!timezone) errors.timezone = 'Timezone is required.';

                // Validate time fields based on per-day toggle
                if (enable_per_day_timeframes) {
                    // Import validation function at top of file
                    const { validateAllDayTimeframes, hasTimeframeErrors } = require('./CampaignScheduleFields/timeRangeValidation');
                    const timeframeErrors = validateAllDayTimeframes(day_timeframes, formData.days_of_week);

                    if (hasTimeframeErrors(timeframeErrors)) {
                        errors.day_timeframes = timeframeErrors;
                    }
                } else {
                    // Standard validation for single start/end times
                    if (!start_time) errors.start_time = 'Start time is required.';
                    if (!end_time) errors.end_time = 'End time is required.';
                }

                if (start_date && start_time && timezone) {
                    if (!isEditing) {
                        // Validate only in create mode:
                        // Ensure the selected start date is not in the past (based on timezone)
                        if (isDateInPast(start_date, timezone)) {
                            errors.start_date = "Start date should not be in the past.";
                        }
                    }
                }
            }
        }

        return errors;
    };


    function transformLaravelErrors(errors) {
        const formErrors = {};
        const nestedCustomerErrors = [];

        Object.keys(errors).forEach((key) => {
            const messages = errors[key];
            if (key.startsWith('customers.')) {
                const match = key.match(/^customers\.(\d+)\.(.+)$/);
                if (match) {
                    const index = parseInt(match[1], 10);
                    const field = match[2];
                    nestedCustomerErrors.push({ index, field, message: messages });
                }
            } else {
                formErrors[key] = messages;
            }
        });
        return { formErrors, nestedCustomerErrors };
    }

    const handleSave = async () => {
        if (saving) {
            return;
        }

        // AC14: Validate Trust Hub profile status before campaign creation
        // DISABLED: Trust Hub validation bypassed
        // const isTrustHubValid = await validateTrustHub();
        // if (!isTrustHubValid) {
        //     // Modal will be shown automatically by the hook
        //     return;
        // }

        setSaving(true);
        setLoading(true);
        setFormErrors({});
        setErrors([]);
        setMessage(null);

        const campaignErrors = validateForm();

        if (Object.keys(campaignErrors).length > 0) {
            setFormErrors(campaignErrors);
            setSaving(false);
            setLoading(false);
            // Scroll to first error after DOM updates
            setTimeout(() => {
                scrollToFirstError(campaignErrors);
            }, 100);
            return;
        }
        const campaignData = formData;
        if (campaignData.type !== 'triggered') {
            // Check if there's at least one customer (manual or CSV)
            const totalCustomers = getTotalCustomers();
            if (totalCustomers === 0) {
                setMessage({ type: 'error', text: 'At least one customer is required to save the campaign.' });
                // Scroll to error message after DOM updates, especially when manual entry section is open
                setTimeout(() => {
                    if (errorMessageRef.current) {
                        errorMessageRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                    }
                }, 150);
                setTimeout(() => setMessage(null), 5000);
                setSaving(false);
                setLoading(false);
                return;
            }
        }
        setFormErrors({});
        setErrors([]);

        const formatDateForAPI = (dateString) => {
            if (!dateString) return null;
            try {
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            } catch (error) {
                console.error('Error formatting date for API:', error);
                return null;
            }
        };

        // Format dates and time for API
        const formattedCampaignData = {
            ...campaignData,
            start_date: formatDateForAPI(campaignData.start_date),
            end_date: formatDateForAPI(campaignData.end_date),
            schedule_type: campaignData.send_type === 'now' ? '' : campaignData.schedule_type,
            days_of_week: campaignData.days_of_week,
        };

        if (formattedCampaignData.type === 'triggered') {
            const d = parseInt(formattedCampaignData.webhook_queue_delay_days, 10);
            formattedCampaignData.webhook_queue_delay_days = Number.isNaN(d)
                ? 0
                : Math.min(365, Math.max(0, d));
            const cgm = formData.call_group_mode;
            formattedCampaignData.call_group_mode =
                cgm === 'webhook_inline'
                    ? 'webhook_inline'
                    : cgm === 'saved_template'
                      ? 'saved_template'
                      : 'individual';
            if (cgm !== 'saved_template') {
                formattedCampaignData.call_group_template_id = null;
            }
        } else {
            formattedCampaignData.webhook_queue_delay_days = 0;
            delete formattedCampaignData.call_group_mode;
            delete formattedCampaignData.call_group_template_id;
        }

        if (formattedCampaignData.send_type === 'now') {
            formattedCampaignData.duplicate_data_rule_enabled = false;
            formattedCampaignData.duplicate_data_rule_days = null;
            formattedCampaignData.enable_per_day_timeframes = false;
            formattedCampaignData.day_timeframes = {};
        } else if (formattedCampaignData.duplicate_data_rule_enabled) {
            const d = parseInt(formattedCampaignData.duplicate_data_rule_days, 10);
            formattedCampaignData.duplicate_data_rule_days = Number.isNaN(d)
                ? null
                : Math.min(
                      DUPLICATE_DATA_RULE_MAX_DAYS,
                      Math.max(1, d)
                  );
        } else {
            formattedCampaignData.duplicate_data_rule_enabled = false;
            formattedCampaignData.duplicate_data_rule_days = null;
        }

        // Clean up timeframe data if not enabled
        if (!formattedCampaignData.enable_per_day_timeframes) {
            formattedCampaignData.day_timeframes = {};
        }

        const customersForApi = customers.map(({ pending_date_added, ...c }) => c);

        const campaignDataWithCustomers = {
            ...formattedCampaignData,
            // Triggered campaigns may have no rows yet (webhook supplies leads); omit empty array so APIs do not validate `customers` as required.
            ...(formattedCampaignData.type !== 'triggered' || customersForApi.length > 0
                ? { customers: customersForApi }
                : {}),
            workspace_id: formattedCampaignData.workspace_id,
            outbound_twilio_number_id: formattedCampaignData.outbound_twilio_number_id
                ? parseInt(formattedCampaignData.outbound_twilio_number_id, 10)
                : null,
            caller_id_id: formattedCampaignData.caller_id_id
                ? parseInt(formattedCampaignData.caller_id_id, 10)
                : null,
            user_id: parseInt(user_id),
            timezone: formattedCampaignData.timezone
        };

        try {
            if (onSave) {
                const result = await onSave(campaignDataWithCustomers);
                if (result && !result.status) {
                    if (result.errors && typeof result.errors === 'object') {
                        const { formErrors, nestedCustomerErrors } = transformLaravelErrors(result.errors);
                        setFormErrors(formErrors);
                        setErrors(nestedCustomerErrors);
                        // Scroll to first error after DOM updates
                        setTimeout(() => {
                            // If there are form field errors, scroll to the first one
                            if (Object.keys(formErrors).length > 0) {
                                scrollToFirstError(formErrors);
                            }
                            // Otherwise, if there are nested customer errors, scroll to that message
                            else if (nestedCustomerErrors.length > 0 && nestedErrorsRef.current) {
                                nestedErrorsRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'nearest'
                                });
                            }
                            // Otherwise, scroll to general error message if it exists
                            else if (errorMessageRef.current) {
                                errorMessageRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'nearest'
                                });
                            }
                        }, 150);
                    } else if (result.message) {
                        setMessage({ type: 'error', text: result.message });
                        // Scroll to error message after DOM updates
                        setTimeout(() => {
                            if (errorMessageRef.current) {
                                errorMessageRef.current.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'nearest'
                                });
                            }
                        }, 150);
                        setTimeout(() => setMessage(null), 5000);
                    }
                }
            }
        } catch (error) {
            console.log(error);
            // setMessage({ type: 'error', text: 'An error occurred while saving the campaign. Please try again.' });
            setTimeout(() => setMessage(null), 5000);
        } finally {
            setSaving(false);
            setLoading(false);
        }
    };
    const isEditing = !!editingCampaign;
    const canEditCallGroupMode = canEditField('call_group_mode', isEditing, formData);
    const showCallGroupColumns =
        formData.type === 'triggered' &&
        (formData.call_group_mode === 'webhook_inline' || formData.call_group_mode === 'saved_template');
    return (
        <div className={compactMode ? "" : "yr-chat-round-box addEditBranch"} style={{
            ...(compactMode && {
                border: 'none',
                boxShadow: 'none',
                borderRadius: '0',
                padding: '0',
                margin: '0'
            })
        }}>

            <div className="row">
                <div className="col-md-6 text-left">
                    <button
                        className="linear-gradient yr-submitbtn btn-sm"
                        onClick={onCancel}
                        style={{
                            border: '1px solid #6c757d',
                            color: '#6c757d',
                            borderRadius: '5px',
                            padding: '8px 16px',
                            fontSize: '14px'
                        }}
                    >
                        <i className="la la-arrow-left mr-2"></i>
                        Back To List
                    </button>
                </div>
            </div>

            <div className="row mt-4">
                <div className="add-branch-title mb-3 col-md-6">
                    {editingCampaign ? 'Edit Campaign' : 'Add Campaign'}
                </div>
            </div>
            <div className="row">
                <div className="col-md-7">
                    {message && (
                        <div ref={errorMessageRef}>
                            <Message
                                className="campaign-page-message"
                                color={
                                    message.type === "success"
                                        ? "green"
                                        : message.type === "info"
                                            ? "blue"
                                            : "red"
                                }
                                style={{ display: message ? "block" : "none", marginTop: 30 }}
                            >
                                {message?.text}
                            </Message>
                        </div>
                    )}
                    {errors.length > 0 && (
                        <div ref={nestedErrorsRef}>
                            <Message color="red" style={{ marginTop: 30 }}>
                                <Message.List>
                                    {errors.map((err, i) => (
                                        <Message.Item key={i}>
                                            Row {err.index + 1}: {err.message}
                                        </Message.Item>
                                    ))}
                                </Message.List>
                            </Message>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group" style={{ marginBottom: 10 }}>
                                <label className="pd10botbuilder">
                                    Outbound Number<span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="outbound_number_combined"
                                    data-field="outbound_number_combined"
                                    className={`form-control ${(formErrors.outbound_twilio_number_id || formErrors.caller_id_id) ? 'is-invalid' : ''}`}
                                    value={
                                        formData.caller_id_id
                                            ? `callerid:${formData.caller_id_id}`
                                            : formData.outbound_twilio_number_id
                                                ? `outbound:${formData.outbound_twilio_number_id}`
                                                : ''
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setFormErrors({});
                                        setErrors([]);
                                        if (!val) {
                                            setFormData(prev => ({
                                                ...prev,
                                                outbound_twilio_number_id: '',
                                                caller_id_id: '',
                                            }));
                                        } else if (val.startsWith('callerid:')) {
                                            const callerIdId = val.replace('callerid:', '');
                                            const fallbackOutbound = availableOutboundNumbers[0]?.id ?? '';
                                            setFormData(prev => {
                                                const resolvedOutbound = prev.outbound_twilio_number_id || fallbackOutbound;
                                                return {
                                                    ...prev,
                                                    caller_id_id: callerIdId,
                                                    outbound_twilio_number_id: resolvedOutbound,
                                                };
                                            });
                                        } else {
                                            const outboundId = val.replace('outbound:', '');
                                            setFormData(prev => ({
                                                ...prev,
                                                outbound_twilio_number_id: outboundId,
                                                caller_id_id: '',
                                            }));
                                        }
                                    }}
                                    disabled={outboundNumbersLoading || callerIdsLoading}
                                    style={{
                                        border: (formErrors.outbound_twilio_number_id || formErrors.caller_id_id) ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                        borderRadius: '5px',
                                        padding: '10px 15px',
                                        fontSize: '14px',
                                        color: '#000'
                                    }}
                                >
                                    <option value="">
                                        {(outboundNumbersLoading || callerIdsLoading) ? 'Loading numbers...' : 'Select Outbound Number'}
                                    </option>
                                    {availableCallerIds.length > 0 && (
                                        <>
                                            <option disabled>Verified Caller IDs</option>
                                            {availableCallerIds.map((callerId) => {
                                                const digits = (callerId.phone_number || '').replace(/\D/g, '');
                                                const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                                                const formatted = ten.length === 10
                                                    ? `+1(${ten.slice(0,3)}) ${ten.slice(3,6)}-${ten.slice(6)}`
                                                    : callerId.phone_number;
                                                return (
                                                    <option key={`callerid:${callerId.id}`} value={`callerid:${callerId.id}`}>
                                                        {callerId.friendly_name
                                                            ? `${callerId.friendly_name} ${formatted}`
                                                            : formatted}
                                                    </option>
                                                );
                                            })}
                                        </>
                                    )}
                                    {availableOutboundNumbers.length > 0 && (
                                        <>
                                            <option disabled>Purchased Numbers</option>
                                            {availableOutboundNumbers.map((number) => {
                                                const digits = (number.phone || '').replace(/\D/g, '');
                                                const ten = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                                                const formatted = ten.length === 10
                                                    ? `+1(${ten.slice(0,3)}) ${ten.slice(3,6)}-${ten.slice(6)}`
                                                    : number.phone;
                                                return (
                                                    <option key={`outbound:${number.id}`} value={`outbound:${number.id}`}>
                                                        {formatted}
                                                    </option>
                                                );
                                            })}
                                        </>
                                    )}
                                </select>
                                {outboundNumbersMessage && availableOutboundNumbers.length === 0 && !outboundNumbersLoading && (
                                    <div className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                                        {outboundNumbersMessage}
                                    </div>
                                )}
                                {callerIdsMessage && availableCallerIds.length === 0 && !callerIdsLoading && (
                                    <div className="text-muted" style={{ fontSize: '12px', marginTop: '5px' }}>
                                        {callerIdsMessage}{' '}
                                        <a href="/trust-hub?tab=phones" style={{ color: '#087ab6' }}>Go to Trust Hub</a>
                                    </div>
                                )}
                                {(formErrors.outbound_twilio_number_id || formErrors.caller_id_id) && (
                                    <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                        {formErrors.outbound_twilio_number_id || formErrors.caller_id_id}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group" style={{ marginBottom: 10 }}>
                                <label className="pd10botbuilder">
                                    Campaign Name<span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    data-field="name"
                                    className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                    placeholder="Enter campaign name"
                                    value={formData.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    disabled={!canEditField('name', isEditing)}
                                    style={{
                                        border: formErrors.name ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                        borderRadius: '5px',
                                        padding: '10px 15px',
                                        fontSize: '14px'
                                    }}
                                />
                                {formErrors.name && (
                                    <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                        {formErrors.name}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group" style={{ marginBottom: 10 }}>
                                <label className="pd10botbuilder">
                                    Workspace<span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="workspace_id"
                                    data-field="workspace_id"
                                    className={`form-control ${formErrors.workspace_id ? 'is-invalid' : ''}`}
                                    value={formData.workspace_id || ''}
                                    onChange={(e) => handleInputChange('workspace_id', e.target.value)}
                                    disabled={!canEditField('workspace_id', isEditing)}
                                    style={{
                                        border: formErrors.workspace_id ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                        borderRadius: '5px',
                                        padding: '10px 15px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select Workspace</option>
                                    {activeOutboundWorkspaces.map((workspace) => (
                                        <option
                                            key={workspace.id}
                                            value={workspace.id}
                                        >
                                            {workspace.name}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.workspace_id && (
                                    <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                        {formErrors.workspace_id}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group" style={{ marginBottom: 10 }}>
                                <label className="pd10botbuilder">
                                    Campaign Category<span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="campaign_category"
                                    data-field="campaign_category"
                                    className={`form-control ${formErrors.campaign_category ? 'is-invalid' : ''}`}
                                    value={formData.campaign_category || ''}
                                    onChange={(e) => handleInputChange('campaign_category', e.target.value)}

                                    style={{
                                        border: formErrors.campaign_category ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                        borderRadius: '5px',
                                        padding: '10px 15px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select Campaign Category</option>
                                    <option value="non_marketing">General Non-Marketing</option>
                                    <option value="marketing">Marketing</option>
                                </select>
                                {formErrors.campaign_category && (
                                    <div
                                        className="error invalid-feedback d-block"
                                        style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}
                                    >
                                        {formErrors.campaign_category}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="form-group" style={{ marginBottom: 10 }}>
                                <label className="pd10botbuilder">
                                    Consent Type<span style={{ color: 'red' }}>*</span>
                                </label>
                                <select
                                    name="consent_type"
                                    data-field="consent_type"
                                    className={`form-control ${formErrors.consent_type ? 'is-invalid' : ''}`}
                                    value={formData.consent_type || ''}
                                    onChange={(e) => handleInputChange('consent_type', e.target.value)}
                                    style={{
                                        border: formErrors.consent_type ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                        borderRadius: '5px',
                                        padding: '10px 15px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Select Consent Type</option>
                                    { constants.CONSENT_TYPE_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.consent_type && (
                                    <div
                                        className="error invalid-feedback d-block"
                                        style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}
                                    >
                                        {formErrors.consent_type}
                                    </div>
                                )}
                            </div>
                            {formData.consent_type === 'other' && (
                                <div className="form-group" style={{ marginBottom: 10 }}>
                                    <label className="pd10botbuilder">
                                        Other (Describe)<span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <textarea
                                        name="consent_notes"
                                        data-field="consent_notes"
                                        className={`form-control ${formErrors.consent_notes ? 'is-invalid' : ''}`}
                                        value={formData.consent_notes || ''}
                                        onChange={(e) => handleInputChange('consent_notes', e.target.value)}
                                        placeholder="Describe how consent was obtained"
                                        minLength={10}
                                        maxLength={500}
                                        style={{
                                            border: formErrors.consent_notes ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                            borderRadius: '5px',
                                            padding: '10px 15px',
                                            fontSize: '14px',
                                            resize: 'vertical',
                                            minHeight: '100px',
                                            maxHeight: '150px'                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                        {formData.consent_notes?.length || 0} / 500 characters
                                    </div>
                                    {formErrors.consent_notes && (
                                        <div
                                            className="error invalid-feedback d-block"
                                            style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}
                                        >
                                            {formErrors.consent_notes}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Campaign Type*/}
                    <RadioOptionGroup
                        label="Campaign Type"
                        name="campaignType"
                        selectedValue={formData.type}
                        onChange={(value) => handleInputChange('type', value)}
                        options={[
                            {
                                value: 'manual',
                                label: 'Manual',
                                description: 'Start campaign now or schedule for later.',
                                disabled: !canEditField('campaignType', isEditing)

                            },
                            {
                                value: 'triggered',
                                label: 'Triggered',
                                description: 'Triggered via webhooks from your website or forms.',
                                disabled: !canEditField('campaignType', isEditing)

                            }
                        ]}
                    />

                    {formData.type === 'triggered' && (
                        <div className="row">
                            <div className="col-12">
                                <Message info style={{ marginTop: "1rem" }}>
                                    <Icon name="info circle" />
                                    A unique webhook will be generated to connect your data source.{" "}
                                    <strong>Pre-built integrations are coming soon.</strong>
                                </Message>
                            </div>
                        </div>
                    )}
                    {/* Customer Management Section*/}
                    <div className="row">
                        <div className="col-12">
                            <div className="customer-manager-container" style={{ marginTop: '10px' }}>
                                {formData.type !== 'triggered' && (
                                    <div className="d-flex align-items-center justify-content-start mb-1">
                                        <div className="d-flex" style={{ gap: '1rem' }}>
                                            <button
                                                className="btn add-link"
                                                style={{
                                                    fontSize: '14px'
                                                }}
                                                onClick={() => setShowManualEntry(true)}
                                            >
                                                Add Customer Manually
                                            </button>

                                            <button
                                                className="btn add-link"
                                                style={{
                                                    fontSize: '14px'
                                                }}
                                                onClick={() => setShowCSVModal(true)}
                                            >
                                                Upload CSV
                                            </button>
                                            <CSVSampleDownloadButton
                                                filename="campaign_customers_sample.csv"
                                                headers={headers}
                                                sampleData={sampleData}
                                            />

                                        </div>
                                    </div>
                                )}

                                {/* Manual Customer Entry*/}

                                {showManualEntry && (
                                    <ManualCustomerEntry
                                        show={showManualEntry}
                                        manualCustomers={manualCustomers}
                                        errors={errors}
                                        handleChange={handleChange}
                                        handleAddCustomer={handleAddCustomer}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Send Type*/}

                    <RadioOptionGroup
                        label="Choose when to send"
                        name="sendType"
                        selectedValue={formData.send_type}
                        onChange={(value) => handleInputChange('send_type', value)}
                        columns={6}
                        options={[
                            {
                                value: 'now',
                                label: 'Send Now',
                                description: formData.type === 'triggered'
                                    ? 'Disabled for triggered campaigns.'
                                    : 'Send the campaign instantly.',
                                disabled: formData.type === 'triggered' || !canEditField('campaignType', isEditing)
                            },
                            {
                                value: 'scheduled',
                                label: 'Scheduled',
                                description: 'Send at specific time.',
                                disabled: !canEditField('campaignType', isEditing)
                            }
                        ]}
                    />



                    {/* Recurrence Settings */}
                    {formData.send_type === 'scheduled' && (
                        <>
                            <RadioOptionGroup
                                label="Schedule Type"
                                name="schedule_type"
                                selectedValue={formData.schedule_type}
                                onChange={(value) => handleInputChange('schedule_type', value)}
                                columns={6}
                                options={[
                                    {
                                        value: 'one_time',
                                        label: 'One Time',
                                        description: 'Send once at scheduled time.',
                                        disabled: !canEditField('campaignType', isEditing)
                                    },
                                    {
                                        value: 'ongoing',
                                        label: 'Ongoing',
                                        description: 'The campaign stays active as new data is added.',
                                        disabled: !canEditField('campaignType', isEditing)
                                    }
                                ]}
                            />
                            {/* {formData.schedule_type === 'one_time' && (
                                <OneTimeScheduleFields
                                    formData={{...formData}}
                                    formErrors={formErrors}
                                    handleInputChange={handleInputChange}
                                    isEditing={isEditing}
                                    canEditField={canEditField}
                                />
                            )} */}
                            {/* Days of Week (for ongoing)*/}
                            {(formData.schedule_type === 'ongoing' || formData.schedule_type === 'one_time') && (
                                <>
                                    <div className="row">
                                        <div className="col-md-12">
                                            <WeekdaySelector
                                                selectedDays={formData.days_of_week}
                                                onChange={(value) => handleInputChange('days_of_week', value)}
                                                error={formErrors.days_of_week}
                                            />
                                        </div>
                                    </div>

                                    {/* Holiday Exclusion Section */}
                                    <HolidayExclusionSection
                                        holidays={formData.holidays}
                                        onChange={(value) => handleInputChange('holidays', value)}
                                        disabled={!canEditField('holidays', isEditing)}
                                    />
                                    {formErrors.holidays && (
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="error invalid-feedback d-block" style={{ color: '#dc3545', fontSize: '14px', marginTop: '10px', marginBottom: '10px' }}>
                                                    {formErrors.holidays}
                                                </div>
                                            </div>
                                        </div>
                                    )}



                                    {/* Always show global time window fields */}
                                    <DailyWeeklyScheduleFields
                                        formData={{ ...formData }}
                                        formErrors={formErrors}
                                        handleInputChange={handleInputChange}
                                        isEditing={isEditing}
                                        canEditField={canEditField}
                                        setLoading={setLoading}
                                    />

                                    {/* Edit Campaign Time Frames by Day
 Section */}
                                    <div className="row" style={{ marginTop: '30px' }}>
                                        <div className="col-12">
                                            <div className="mb-3 pb-2" style={{ borderBottom: '2px solid #e1e5e9' }}>
                                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                                                    Edit Campaign Time Frames by Day

                                                </h3>
                                            </div>

                                            <div className="voicemail-retry-settings">
                                                <div className="row">
                                                    <div className="col-md-12">
                                                        <div className="form-group voicemail-retry-toggle">
                                                            <div className="voicemail-retry-toggle-wrapper">
                                                                <label className="voicemail-retry-toggle-label mb-0">
                                                                    Per-day time frames

                                                                </label>

                                                                <span
                                                                    className={
                                                                        formData.enable_per_day_timeframes
                                                                            ? "kt-switch kt-switch-sm kt-switch--success chat-cus-check voicemail-retry-switch"
                                                                            : "kt-switch kt-switch-sm kt-switch--error chat-cus-check voicemail-retry-switch"
                                                                    }
                                                                >
                                                                    <label>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={formData.enable_per_day_timeframes}
                                                                            onChange={(e) => handleInputChange('enable_per_day_timeframes', e.target.checked)}
                                                                        />
                                                                        <span />
                                                                    </label>
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <p className="rules-match-voicemail-desc">
                                                            Configure different time frames for each day of the week
                                                        </p>

                                                        {formData.enable_per_day_timeframes && (
                                                            <div className="voicemail-retry-settings-container mt-3">
                                                                <PerDayTimeFrameEditor
                                                                    selectedDays={formData.days_of_week}
                                                                    dayTimeframes={formData.day_timeframes}
                                                                    onChange={(value) => handleInputChange('day_timeframes', value)}
                                                                    errors={formErrors.day_timeframes || {}}
                                                                    globalStartTime={formData.start_time}
                                                                    globalEndTime={formData.end_time}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>

                            )}

                        </>
                    )}

                    {/* Webhook queue delay — triggered campaigns only (same heading treatment as Voicemail Retry Settings). */}
                    {formData.type === 'triggered' && (
                        <div className="row" style={{ marginTop: '30px' }}>
                            <div className="col-12">
                                <div className="mb-3 pb-2" style={{ borderBottom: '2px solid #e1e5e9' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                                        Webhook Queue Delay
                                    </h3>
                                </div>
                                <div className="row">
                                        <div className="col-12">
                                            <div className="voicemail-retry-settings webhook-queue-delay-block">
                                                <div className="webhook-queue-delay-block__subrow">
                                                    <p className="webhook-queue-delay-block__desc">
                                                        Days to wait before a webhook lead enters the active call queue.
                                                        Manual and CSV contacts skip this delay.
                                                    </p>
                                                    <div
                                                        className={`webhook-queue-delay-block__control${
                                                            formErrors.webhook_queue_delay_days
                                                                ? ' webhook-queue-delay-block__control--error'
                                                                : ''
                                                        }`}
                                                    >
                                                        <label className="sr-only" htmlFor="webhook_queue_delay_days">
                                                            Webhook queue delay (days)
                                                        </label>
                                                        <input
                                                            id="webhook_queue_delay_days"
                                                            type="number"
                                                            min={0}
                                                            max={365}
                                                            className="form-control"
                                                            aria-invalid={!!formErrors.webhook_queue_delay_days}
                                                            value={formData.webhook_queue_delay_days ?? 0}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '') {
                                                                    handleInputChange('webhook_queue_delay_days', '');
                                                                    return;
                                                                }
                                                                const n = parseInt(v, 10);
                                                                if (Number.isNaN(n)) return;
                                                                handleInputChange(
                                                                    'webhook_queue_delay_days',
                                                                    Math.min(365, Math.max(0, n))
                                                                );
                                                            }}
                                                            disabled={!canEditField('webhook_queue_delay_days', isEditing, formData)}
                                                            style={{
                                                                boxSizing: 'border-box',
                                                                fontSize: '14px',
                                                                lineHeight: 1.25,
                                                                width: 72,
                                                                minWidth: 72,
                                                            }}
                                                        />
                                                        <span className="webhook-queue-delay-block__days">days</span>
                                                    </div>
                                                </div>
                                                {formErrors.webhook_queue_delay_days && (
                                                    <div
                                                        className="invalid-feedback d-block mt-2"
                                                        style={{ color: '#dc3545', fontSize: '12px' }}
                                                    >
                                                        {formErrors.webhook_queue_delay_days}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                            </div>
                        </div>
                    )}


                    {/* Voicemail Callback Rule Section */}
                    <div className="row mt-4 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                        <div className="col-12">
                            <div className="mb-3 pb-2" style={{ borderBottom: '2px solid #e1e5e9' }}>
                                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                                    Voicemail Retry Settings
                                </h3>
                            </div>

                            <VoicemailRetrySettings
                                    formData={formData}
                                    onChange={handleInputChange}
                                    errors={formErrors}
                                    disabled={false}
                                />
                        </div>
                    </div>

                    {/* Duplicate Data Rule — hidden for Send Now (queue rules do not apply) */}
                    {formData.send_type !== 'now' && (
                        <div className="row" style={{ marginTop: '30px' }}>
                            <div className="col-12">
                                <div className="mb-3 pb-2" style={{ borderBottom: '2px solid #e1e5e9' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                                        Duplicate Data Rule
                                    </h3>
                                </div>
                                <DuplicateDataRuleSection
                                    enabled={formData.duplicate_data_rule_enabled}
                                    days={formData.duplicate_data_rule_days}
                                    onEnabledChange={(v) =>
                                        handleInputChange('duplicate_data_rule_enabled', v)
                                    }
                                    onDaysChange={(v) =>
                                        handleInputChange('duplicate_data_rule_days', v)
                                    }
                                    disabled={false}
                                    daysError={formErrors.duplicate_data_rule_days}
                                    idPrefix="campaign-form-dup"
                                />
                                {formData.type === 'triggered' && (
                                    <div style={{ marginTop: '30px' }}>
                                        <div className="mb-3 pb-2" style={{ borderBottom: '2px solid #e1e5e9' }}>
                                            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                                                Call Group Webhook
                                            </h3>
                                        </div>
                                        <CallGroupWebhookToggleSection
                                            acceptCallGroup={
                                                formData.call_group_mode === 'webhook_inline' ||
                                                formData.call_group_mode === 'saved_template'
                                            }
                                            onAcceptChange={(v) => {
                                                if (!canEditCallGroupMode) return;
                                                if (!v) {
                                                    handleInputChange('call_group_mode', 'individual');
                                                    return;
                                                }
                                                const cur = formData.call_group_mode;
                                                handleInputChange(
                                                    'call_group_mode',
                                                    cur === 'saved_template' ? 'saved_template' : 'webhook_inline'
                                                );
                                            }}
                                            disabled={!canEditCallGroupMode}
                                            idPrefix="campaign-form-call-group"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div className="col-md-5">
                    <CustomerTable
                        customers={customers}
                        customersPage={customersPage}
                        customersPerPage={customersPerPage}
                        getTotalCustomers={getTotalCustomers}
                        viewCustomer={viewCustomer}
                        setCustomersPage={setCustomersPage}
                        onOptOutChange={handleOptOutChange}
                        isCampaignSaved={isEditing}
                        showCallGroupColumns={showCallGroupColumns}
                        bulkSelection={bulkSelection}
                    />

                </div>

            </div>

            <div className="row mt-3">
                <div className="col-12">
                    <button
                        className="btn btn-sm linear-gradient yr-submitbtn"
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {isEditing ? "Update" : "Save"}
                    </button>
                </div>
            </div>
            <Modal
                show={showCustomerDetailsModal}
                maxWidth="820px"
                title="Customer Details"
                headerClassName="linear-gradient"
                onclose={() => {
                    setShowCustomerDetailsModal(false);
                    setSelectedCustomer(null);
                }}
                body={
                    <div style={{ padding: '12px 20px 20px', textAlign: 'left', minWidth: 0 }}>
                        <CustomerDetailsModalBody
                            selectedCustomer={selectedCustomer}
                            phoneDisplay={
                                selectedCustomer?.phone_number
                                    ? `+1${selectedCustomer.phone_number}`
                                    : 'N/A'
                            }
                            showEmptyDynamicVariablesMessage={false}
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2 ov-cd-modal-footer">
                        <button
                            type="button"
                            className="btn linear-gradient yr-submitbtn btn-sm"
                            onClick={() => {
                                setShowCustomerDetailsModal(false);
                                setSelectedCustomer(null);
                            }}
                        >
                            Close
                        </button>
                    </div>
                }
            />
            <Modal
                show={showCSVModal}
                maxWidth="600px"
                title="Upload Customers"
                onclose={() => {
                    setShowCSVModal(false);
                    setCSVFile(null);
                    setCSVError("");
                    setCSVSuccess("");
                    if (fileInputRef.current) {
                        fileInputRef.current.value = null;
                    }
                }}
                body={
                    <div className="compaign-form-detail">
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 12, lineHeight: 1.4 }}>
                            Spaces in CSV column names are removed (e.g. &quot;Policy Number&quot; → PolicyNumber). Use <code style={{ background: '#f1f3f4', padding: '1px 4px', borderRadius: 3 }}>{'{{'}VariableName{'}}'}</code> in your workspace to insert the value during calls.
                        </div>
                        {csvSuccess && (
                            <Message
                                color="green"
                                style={{ display: csvSuccess ? "block" : "none", marginTop: 30 }}
                            >
                                {csvSuccess}
                            </Message>
                        )}
                        {csvError && (
                            <div style={{ color: 'red', marginBottom: '10px' }}>
                                {Array.isArray(csvError) ? (
                                    <div>
                                        <ul style={{ margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                            {csvError.map((err, i) => (
                                                <li key={i} style={{ marginBottom: '4px' }}>
                                                    {typeof err === "string" ? (
                                                        err
                                                    ) : (
                                                        <>
                                                            {err.row ? `Row ${err.row}: ` : ""}
                                                            {Array.isArray(err.errors)
                                                                ? err.errors.join(", ")
                                                                : err.errors || err.messages || JSON.stringify(err)}
                                                        </>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <span>{csvError}</span>
                                )}
                            </div>
                        )}

                        {csvProcessing && <div className="ui loading form"></div>}
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    setCSVFile(e.target.files[0]);
                                    setCSVError("");
                                    setCSVSuccess("");
                                }
                            }}
                            className="form-control my-3"
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCSVModal(false);
                                clearCSVInputAndErrors();
                            }}
                            className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        >
                            <span>Cancel</span>
                        </button>

                        <button
                            type="button"
                            onClick={handleCSVUpload}
                            disabled={loading || csvProcessing || csvError || csvSuccess}
                            className="btn linear-gradient yr-submitbtn btn-sm"
                            style={{
                                opacity: csvProcessing ? 0.6 : 1,
                                cursor: csvProcessing ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {csvProcessing ? "Processing..." : "Save"}
                        </button>
                    </div>
                }
            />

            <ConfirmationModal
                open={showBulkConfirmModal}
                onClose={() => setShowBulkConfirmModal(false)}
                onConfirm={confirmBulkDelete}
                title="Delete selected contacts"
                content={`You are about to permanently delete ${bulkSelection?.selectedKeys?.length ?? 0} contact(s) from this campaign. This cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel="Delete"
            />
            <ConfirmationModal
                open={showOptOutConfirmModal}
                onClose={() => {
                    setShowOptOutConfirmModal(false);
                    setOptOutReenableCustomer(null);
                }}
                onConfirm={confirmOptOutReenable}
                title="Remove from Internal DNC?"
                content="This caller previously opted out of all calling communication. Are you sure you want to remove this number from the Internal DNC list and allow outbound calls again?"
                cancelLabel="Cancel"
                confirmLabel="Confirm"
            />

            {/* AC14: Trust Hub Validation Modal */}
            {/* DISABLED: Trust Hub validation modal */}
            {/* {showTrustHubModal && (
                <TrustHubValidationModal
                    onClose={() => setShowTrustHubModal(false)}
                    profileStatus={trustHubProfileStatus}
                />
            )} */}

            {(loading || saving) && <div className="ui loading form"></div>}
        </div>
    );
};

export default CampaignForm; 